import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, Pencil, Check, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d[0]?.msg || 'Datos inválidos';
  return 'Error al guardar el PIN';
};

function PinRow({ u, onSaved }) {
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!/^\d{4}$/.test(value)) return toast.error('El PIN debe tener exactamente 4 dígitos');
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/pins/${u.id}`, { pin_code: value });
      toast.success(`PIN de ${u.first_name} actualizado`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`pin-row-${u.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {u.first_name} {u.last_name}
          {u.is_master_admin && <Badge className="ml-2 bg-slate-900 text-white text-[10px]">Master</Badge>}
          {u.is_active === false && <Badge variant="outline" className="ml-2 text-[10px]">Inactivo</Badge>}
        </p>
        <p className="text-xs text-slate-500 truncate">{u.email} · {u.role}</p>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            inputMode="numeric"
            maxLength={4}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            className="w-24 font-mono tracking-widest text-center"
            placeholder="••••"
            data-testid={`pin-input-${u.id}`}
          />
          <Button size="icon" onClick={save} disabled={saving} data-testid={`pin-save-${u.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setEditing(false)} data-testid={`pin-cancel-${u.id}`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-mono tracking-[0.3em] w-20 text-center text-slate-800" data-testid={`pin-value-${u.id}`}>
            {u.pin_code ? (visible ? u.pin_code : '••••') : <span className="text-xs tracking-normal text-slate-400">Sin PIN</span>}
          </span>
          <Button size="icon" variant="ghost" disabled={!u.pin_code} onClick={() => setVisible((v) => !v)} data-testid={`pin-toggle-${u.id}`}>
            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button size="icon" variant="outline" onClick={() => { setValue(u.pin_code || ''); setEditing(true); }} data-testid={`pin-edit-${u.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export const PinManagerTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/pins`);
      setUsers(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="pin-manager">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <KeyRound className="w-4 h-4 text-amber-600" />
        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>PINs de acceso</h3>
        <span className="text-xs text-slate-500 ml-auto">Solo visible para el Master Admin</span>
      </div>
      {loading ? (
        <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        users.map((u) => <PinRow key={u.id} u={u} onSaved={load} />)
      )}
    </div>
  );
};
